// Activity queries for the OnboardingBaseline cohort. Re-uses the cohort
// selection CTEs from BASELINE_SQL (via COHORT_CTES_SQL) so the activity rows
// match the same baseline definition — no jsonb-array parameter, which the
// admin endpoint rejects as a scalar (22023: cannot extract elements from a
// scalar). Two queries:
//
//   ACTIVITY_RAW_24H_SQL   — every event in first 24h post-signup, with
//                            minutes_since_signup. Powers 15m / hourly buckets.
//   ACTIVITY_DAILY_90D_SQL — pre-aggregated (user, day_offset, event_type)
//                            for days 0-89. Powers daily / weekly + retention.
//
// Event types are pooled to match the lift table's feature set, minus team
// invites (already modelled as a join-date proxy in the lift table).

import { cohortCtesSqlFor } from "./sql";
import type { CohortMode } from "./sql";

const ALL_EVENTS_CTE = `
cohort_users AS (
  SELECT DISTINCT co.user_external_id, u.created_at_utc AS signup_ts
  FROM cohort co JOIN public.users u ON u.external_id = co.user_external_id
),

all_events AS (
  SELECT t.user_external_id, t.created_date_utc AS event_ts, 'transcript'::text AS event_type
  FROM public.meeting_transcription t
  JOIN public.meeting m ON m.external_id = t.meeting_external_id
  WHERE m.is_demo = false
    AND m.deleted_date_utc IS NULL
    AND t.deleted_date_utc IS NULL
    AND t.user_external_id IN (SELECT user_external_id FROM cohort_users)
  UNION ALL
  SELECT s.user_external_id, s.created_date_utc, 'summary'
  FROM public.meeting_synopsis s
  JOIN public.meeting m ON m.external_id = s.meeting_external_id
  WHERE m.is_demo = false
    AND m.deleted_date_utc IS NULL
    AND s.user_external_id IN (SELECT user_external_id FROM cohort_users)
  UNION ALL
  SELECT t.user_external_id, t.created_date_utc, 'custom_template'
  FROM public.template t
  JOIN public.users tu ON tu.external_id = t.user_external_id
  WHERE t.created_date_utc > tu.created_at_utc + interval '5 minutes'
    AND t.is_archived = false
    AND t.user_external_id IN (SELECT user_external_id FROM cohort_users)
  UNION ALL
  SELECT cal.user_external_id, cal.created_date_utc, 'calendar'
  FROM public.calendar cal
  WHERE cal.user_external_id IN (SELECT user_external_id FROM cohort_users)
  UNION ALL
  SELECT c.user_external_id, c.created_date_utc, 'chat'
  FROM public.chat c
  WHERE c.deleted_date_utc IS NULL
    AND c.user_external_id IN (SELECT user_external_id FROM cohort_users)
  UNION ALL
  SELECT d.user_external_id, d.created_date_utc, 'document'
  FROM public.document d
  WHERE d.user_external_id IN (SELECT user_external_id FROM cohort_users)
  UNION ALL
  SELECT t.created_by_user_external_id, t.created_date_utc, 'task'
  FROM public.task t
  WHERE t.deleted_date_utc IS NULL
    AND t.created_by_user_external_id IN (SELECT user_external_id FROM cohort_users)
  UNION ALL
  SELECT cl.created_by_user_external_id, cl.created_date_utc, 'client'
  FROM public.clients cl
  WHERE cl.deleted_date_utc IS NULL
    AND cl.created_by_user_external_id IN (SELECT user_external_id FROM cohort_users)
  UNION ALL
  SELECT ff.created_by_user_external_id, ff.created_date_utc, 'form'
  FROM public.filled_form ff
  WHERE ff.created_by_user_external_id IN (SELECT user_external_id FROM cohort_users)
  UNION ALL
  SELECT DISTINCT co.user_external_id, cb_plus.first_ts AS event_ts, 'conversion_plus'
  FROM cohort co
  JOIN (
    SELECT company_external_id, MIN(subscription_started_date_utc) AS first_ts
    FROM public.company_billing
    WHERE billing_plan::text = 'plus'
      AND subscription_started_date_utc IS NOT NULL
    GROUP BY 1
  ) cb_plus ON cb_plus.company_external_id = co.company_external_id
  UNION ALL
  SELECT DISTINCT co.user_external_id, cb_pro.first_ts AS event_ts, 'conversion_pro'
  FROM cohort co
  JOIN (
    SELECT company_external_id, MIN(subscription_started_date_utc) AS first_ts
    FROM public.company_billing
    WHERE billing_plan::text = 'pro'
      AND subscription_started_date_utc IS NOT NULL
    GROUP BY 1
  ) cb_pro ON cb_pro.company_external_id = co.company_external_id
)`;

function buildActivityRaw24hSql(mode: CohortMode): string {
  return `
WITH ${cohortCtesSqlFor(mode)},
${ALL_EVENTS_CTE}
SELECT
  ae.user_external_id,
  ae.event_type,
  EXTRACT(EPOCH FROM ae.event_ts - cu.signup_ts) / 60.0 AS minutes_since_signup
FROM all_events ae
JOIN cohort_users cu ON cu.user_external_id = ae.user_external_id
WHERE ae.event_ts >= cu.signup_ts
  AND ae.event_ts <  cu.signup_ts + interval '24 hours'
`;
}

// Bucket the 90d post-signup observation window into 6 named retention buckets:
//   0: Week 1     (days 0-6)
//   1: Week 2     (days 7-13)
//   2: Week 3     (days 14-20)
//   3: Week 4     (days 21-27)
//   4: Weeks 5-8  (days 28-55)
//   5: Weeks 9-12 (days 56-89)
// This keeps the response under the /admin/query row cap (per-user × 6 × event
// types) — weekly aggregation was ~15k rows for the baseline cohort against a
// 10k cap, dropping ~33% of activity rows silently.
function buildActivityDaily90dSql(mode: CohortMode): string {
  return `
WITH ${cohortCtesSqlFor(mode)},
${ALL_EVENTS_CTE}
SELECT
  ae.user_external_id,
  CASE
    WHEN ae.event_ts <  cu.signup_ts + interval '7 days'  THEN 0
    WHEN ae.event_ts <  cu.signup_ts + interval '14 days' THEN 1
    WHEN ae.event_ts <  cu.signup_ts + interval '21 days' THEN 2
    WHEN ae.event_ts <  cu.signup_ts + interval '28 days' THEN 3
    WHEN ae.event_ts <  cu.signup_ts + interval '56 days' THEN 4
    ELSE 5
  END AS bucket_idx,
  ae.event_type,
  count(*)::int AS event_count
FROM all_events ae
JOIN cohort_users cu ON cu.user_external_id = ae.user_external_id
WHERE ae.event_ts >= cu.signup_ts
  AND ae.event_ts <  cu.signup_ts + interval '90 days'
GROUP BY 1, 2, 3
ORDER BY 1, 2, 3
`;
}

export const ACTIVITY_RAW_24H_SQL = buildActivityRaw24hSql("baseline");
export const ACTIVITY_DAILY_90D_SQL = buildActivityDaily90dSql("baseline");
export const ALL_USERS_ACTIVITY_RAW_24H_SQL = buildActivityRaw24hSql("all_users");
export const ALL_USERS_ACTIVITY_DAILY_90D_SQL = buildActivityDaily90dSql("all_users");
export const DEMO_ACTIVITY_RAW_24H_SQL = buildActivityRaw24hSql("demo");
export const DEMO_ACTIVITY_DAILY_90D_SQL = buildActivityDaily90dSql("demo");
export const SUPPORT_NO_DEMO_ACTIVITY_RAW_24H_SQL = buildActivityRaw24hSql("support_no_demo");
export const SUPPORT_NO_DEMO_ACTIVITY_DAILY_90D_SQL = buildActivityDaily90dSql("support_no_demo");
export const SUPPORT_DEMO_ACTIVITY_RAW_24H_SQL = buildActivityRaw24hSql("support_demo");
export const SUPPORT_DEMO_ACTIVITY_DAILY_90D_SQL = buildActivityDaily90dSql("support_demo");

export type ActivityEventType =
  | "transcript" | "summary" | "custom_template" | "calendar"
  | "chat" | "document" | "task" | "client" | "form"
  | "conversion_plus" | "conversion_pro";

export const EVENT_TYPES: ActivityEventType[] = [
  "transcript", "summary", "custom_template", "calendar",
  "chat", "document", "task", "client", "form",
  "conversion_plus", "conversion_pro",
];

export const EVENT_LABEL: Record<ActivityEventType, string> = {
  transcript: "Transcripts",
  summary: "Summaries",
  custom_template: "Custom templates",
  calendar: "Calendar connect",
  chat: "Ask Marloo",
  document: "Documents",
  task: "Tasks",
  client: "Clients",
  form: "Forms",
  conversion_plus: "Conversion to Plus",
  conversion_pro: "Conversion to Pro",
};

export type ActivityRaw24h = {
  user_external_id: string;
  event_type: ActivityEventType;
  minutes_since_signup: number;
};

export type ActivityDaily = {
  user_external_id: string;
  bucket_idx: number;
  event_type: ActivityEventType;
  event_count: number;
};

// Labels and inclusive day boundaries for the 6 retention buckets emitted by
// the daily activity SQL. `maxDay` is the last day still inside the bucket
// (used for horizon/eligibility math).
export const ACTIVITY_BUCKETS: { label: string; maxDay: number }[] = [
  { label: "Week 1",     maxDay: 6 },
  { label: "Week 2",     maxDay: 13 },
  { label: "Week 3",     maxDay: 20 },
  { label: "Week 4",     maxDay: 27 },
  { label: "Weeks 5-8",  maxDay: 55 },
  { label: "Weeks 9-12", maxDay: 89 },
];
export const MAX_BUCKET_IDX = ACTIVITY_BUCKETS.length - 1;
// Bucket index containing a given day-since-signup. Returns -1 if before 0 or
// after the 90d horizon.
export function bucketForDay(day: number): number {
  if (day < 0 || day > 89) return -1;
  if (day < 7)  return 0;
  if (day < 14) return 1;
  if (day < 21) return 2;
  if (day < 28) return 3;
  if (day < 56) return 4;
  return 5;
}

const num = (v: unknown): number => {
  if (v == null) return 0;
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};
const str = (v: unknown): string => (v == null ? "" : String(v));

export function mapRaw24h(columns: string[], rows: unknown[][]): ActivityRaw24h[] {
  const idx: Record<string, number> = {};
  columns.forEach((c, i) => { idx[c] = i; });
  const get = (row: unknown[], k: string): unknown =>
    idx[k] !== undefined ? row[idx[k] as number] : undefined;
  return rows.map((r) => ({
    user_external_id: str(get(r, "user_external_id")),
    event_type: str(get(r, "event_type")) as ActivityEventType,
    minutes_since_signup: num(get(r, "minutes_since_signup")),
  }));
}

export function mapDaily(columns: string[], rows: unknown[][]): ActivityDaily[] {
  const idx: Record<string, number> = {};
  columns.forEach((c, i) => { idx[c] = i; });
  const get = (row: unknown[], k: string): unknown =>
    idx[k] !== undefined ? row[idx[k] as number] : undefined;
  return rows.map((r) => ({
    user_external_id: str(get(r, "user_external_id")),
    bucket_idx: num(get(r, "bucket_idx")),
    event_type: str(get(r, "event_type")) as ActivityEventType,
    event_count: num(get(r, "event_count")),
  }));
}
